import {
  cancelActiveGlobalSeason,
  createGlobalSeasonShell,
  fetchActiveGlobalSeason,
  fetchSeasonRegistrationStatus,
  joinSeasonRegistration,
  lockAndStartGlobalSeason,
  simulateMeetingSession,
} from './api.js';
import { mountDriverLobbyPanel } from './DriverLobbyPanel.js';
import {
  buildBoardHash,
  buildCourseHash,
  getPreferredMeetingCount,
  setPreferredMeetingCount,
} from './seasonPrefs.js';
import {
  trimCalendarToMeetingCount,
  type CircuitPickerOption,
} from './seasonCircuitPickerLogic.js';
import { isCircuitPickerComplete, renderSeasonCircuitPicker } from './renderSeasonCircuitPicker.js';
import { fetchCircuits } from '../circuits/api.js';
import { pickSeasonCircuitCalendar } from '@multirx/shared/game/seasonCircuitCalendar.js';
import { DIVISIONS, DRIVERS_PER_DIVISION } from '@multirx/shared/types/division.js';
import type { DivisionId } from '@multirx/shared/types/division.js';
import { seasonJoinCardAction } from '@multirx/shared/game/seasonRegistrationJoin.js';
import type {
  ActiveSeasonView,
  GlobalActiveSeasonView,
  SeasonMeetingCount,
  SeasonRegistrationStatusView,
} from '@multirx/shared/types/championship.js';
import { SEASON_MEETING_COUNT_OPTIONS } from '@multirx/shared/types/championship.js';
import { createPillButton } from '../ui/pillButton.js';
import { mountEarthLoaderOverlay } from '../ui/earthLoader.js';
import { runSimulateSessionWithDeps } from './championshipSimulate.js';
import { bindChampionshipSimulateDelegation } from './championshipSimulateDelegation.js';
import { showChampionshipPostSessionFlow } from './showChampionshipPostSessionFlow.js';
import { championshipHostKey, resolveLiveChampionshipHost } from './championshipPanelHost.js';
import { beginHostRenderTask, isLatestHostRenderTask } from './hostPaintGeneration.js';
import { enqueueHostRender } from './hostRenderQueue.js';
import { formatDivisionMeetingLabel, formatGlobalMeetingProgress } from './seasonMeetingLabels.js';
import { applyRoleVisibility } from '../auth/navAccount.js';
import { openAlertModal, openConfirmModal } from '../ui/confirmModal.js';
import { getCurrentUser } from '../auth/session.js';
import { appendDivisionCardProfileShell } from './championshipDivisionCard.js';
import { championshipPanelTitle } from './championshipPanelTitle.js';

function showChampionshipAlert(host: HTMLElement, message: string): void {
  void openAlertModal(resolveLiveChampionshipHost(host), {
    title: 'Championnat',
    message,
  });
}

export interface ChampionshipPanelOptions {
  title?: string;
  panelId?: string;
  hostClassName?: string;
  /** Évite un second loader quand le panneau est rafraîchi juste après une action (simulation, etc.). */
  skipInitialLoader?: boolean;
  /** Vue saison déjà chargée (évite un second GET /seasons/active). */
  preloadedGlobalView?: GlobalActiveSeasonView | null;
  /**
   * Actions supplémentaires dans le bandeau (ex. Historique).
   * Appelé après le statut / bouton terminer-abandonner (ou sur empty-state).
   */
  appendHeaderActions?: (
    head: HTMLElement,
    ctx: { globalView: GlobalActiveSeasonView | null }
  ) => void;
}

const simulateInFlightByKey = new Map<string, boolean>();
const panelOptions = new WeakMap<HTMLElement, ChampionshipPanelOptions>();
const panelOptionsByHostKey = new Map<string, ChampionshipPanelOptions>();

function getPanelOptions(host: HTMLElement): ChampionshipPanelOptions {
  return panelOptions.get(host) ?? panelOptionsByHostKey.get(championshipHostKey(host)) ?? {};
}

function setPanelOptions(host: HTMLElement, options: ChampionshipPanelOptions): void {
  panelOptions.set(host, options);
  panelOptionsByHostKey.set(championshipHostKey(host), options);
}

let lobbyOverlay: HTMLElement | null = null;
let lobbyDestroy: (() => void) | null = null;

function closeLobbyOverlay(): void {
  lobbyDestroy?.();
  lobbyDestroy = null;
  lobbyOverlay?.remove();
  lobbyOverlay = null;
}

function openPlayFlow(sessionId: number, opts: { skipModeChoice?: boolean } = {}): void {
  closeLobbyOverlay();
  lobbyOverlay = document.createElement('div');
  lobbyOverlay.className = 'championship-lobby-overlay';
  const inner = document.createElement('div');
  inner.className = 'championship-lobby-overlay-inner';
  const mountHost = document.createElement('div');
  inner.appendChild(mountHost);
  lobbyOverlay.appendChild(inner);
  document.body.appendChild(lobbyOverlay);
  lobbyOverlay.addEventListener('click', (e) => {
    if (e.target === lobbyOverlay) closeLobbyOverlay();
  });

  const launch = (driverId: number): void => {
    closeLobbyOverlay();
    window.location.hash = buildCourseHash(sessionId, driverId);
  };

  const launchWarmup = (driverId: number): void => {
    closeLobbyOverlay();
    window.location.hash = buildBoardHash(sessionId, driverId);
  };

  const showModeStep = (): void => {
    lobbyDestroy?.();
    lobbyDestroy = null;
    mountHost.innerHTML = '';
    const wrap = document.createElement('div');
    wrap.className = 'championship-play-mode';
    const title = document.createElement('h2');
    title.className = 'championship-play-mode-title';
    title.textContent = 'Jouer la manche';
    const lead = document.createElement('p');
    lead.className = 'championship-play-mode-lead';
    lead.textContent = 'Solo contre l’IA ou partie en ligne avec d’autres joueurs ?';
    const actions = document.createElement('div');
    actions.className = 'championship-play-mode-actions';
    const soloBtn = createPillButton('Solo', { variant: 'cta', large: true });
    const onlineBtn = createPillButton('En ligne', { variant: 'soft', large: true });
    const backBtn = createPillButton('Annuler', { variant: 'soft' });
    if (soloBtn instanceof HTMLButtonElement) {
      soloBtn.onclick = () => mountLobby(false);
    }
    if (onlineBtn instanceof HTMLButtonElement) {
      onlineBtn.onclick = () => mountLobby(true);
    }
    if (backBtn instanceof HTMLButtonElement) {
      backBtn.onclick = () => closeLobbyOverlay();
    }
    actions.append(soloBtn, onlineBtn, backBtn);
    wrap.append(title, lead, actions);
    mountHost.appendChild(wrap);
  };

  // Solo et En ligne passent tous deux par le salon socket — solo n’auto-lance
  // plus au clic : confirmation « C'est parti ! » après sélection du pilote.
  const mountLobby = (online: boolean): void => {
    lobbyDestroy?.();
    mountHost.innerHTML = '';
    const panel = mountDriverLobbyPanel(mountHost, {
      sessionId,
      autoLaunchOnSelect: !online,
      onLaunch: launch,
      onWarmupStart: launchWarmup,
      onCancel: opts.skipModeChoice ? closeLobbyOverlay : showModeStep,
    });
    lobbyDestroy = panel.destroy;
  };

  if (opts.skipModeChoice) {
    // Joueur participant à cette manche : une seule voie a du sens (en ligne,
    // le plateau/la course server-autoritaires en dépendent) — pas besoin de
    // lui faire choisir Solo/En ligne.
    mountLobby(true);
  } else {
    showModeStep();
  }
}

/** Admin/modo sur une carte annonçant un plateau dû : ils peuvent jouer
 * n'importe quel pilote, donc on leur laisse le choix Simuler/Jouer plutôt que
 * de deviner à leur place (contrairement à un joueur participant, pour qui
 * `openPlayFlow(..., { skipModeChoice: true })` suffit). */
function openWarmupModeModal(
  sessionId: number,
  divisionLabel: string,
  sessionLabel: string,
  panelHost: HTMLElement
): void {
  closeLobbyOverlay();
  lobbyOverlay = document.createElement('div');
  lobbyOverlay.className = 'championship-lobby-overlay';
  const inner = document.createElement('div');
  inner.className = 'championship-lobby-overlay-inner';
  lobbyOverlay.appendChild(inner);
  document.body.appendChild(lobbyOverlay);
  lobbyOverlay.addEventListener('click', (e) => {
    if (e.target === lobbyOverlay) closeLobbyOverlay();
  });

  const wrap = document.createElement('div');
  wrap.className = 'championship-play-mode';
  const title = document.createElement('h2');
  title.className = 'championship-play-mode-title';
  title.textContent = 'Tour de chauffe';
  const lead = document.createElement('p');
  lead.className = 'championship-play-mode-lead';
  lead.textContent = 'Simuler le plateau et la manche, ou les jouer ?';
  const actions = document.createElement('div');
  actions.className = 'championship-play-mode-actions';
  const simBtn = createPillButton('Simuler', { variant: 'soft', large: true });
  const playBtn = createPillButton('Jouer', { variant: 'cta', large: true });
  const backBtn = createPillButton('Annuler', { variant: 'soft' });
  if (simBtn instanceof HTMLButtonElement) {
    simBtn.onclick = () => {
      closeLobbyOverlay();
      void runSimulateSession(sessionId, divisionLabel, sessionLabel, panelHost);
    };
  }
  if (playBtn instanceof HTMLButtonElement) {
    playBtn.onclick = () => {
      closeLobbyOverlay();
      openPlayFlow(sessionId);
    };
  }
  if (backBtn instanceof HTMLButtonElement) {
    backBtn.onclick = () => closeLobbyOverlay();
  }
  actions.append(simBtn, playBtn, backBtn);
  wrap.append(title, lead, actions);
  inner.appendChild(wrap);
}

function circuitTitleForCard(
  view: ActiveSeasonView,
  next: ActiveSeasonView['nextSession']
): string {
  return (
    next?.circuitName ??
    view.currentMeeting?.circuitName ??
    (next?.circuitId != null ? `Circuit #${next.circuitId}` : 'Circuit à définir')
  );
}

function ensureSimulateDelegation(panelHost: HTMLElement): void {
  bindChampionshipSimulateDelegation(
    panelHost,
    ({ sessionId, divisionLabel, sessionLabel, panelHost: host }) => {
      void runSimulateSession(sessionId, divisionLabel, sessionLabel, host);
    }
  );
}

async function runSimulateSession(
  sessionId: number,
  divisionLabel: string,
  sessionLabel: string,
  panelHost: HTMLElement
): Promise<void> {
  const hostKey = championshipHostKey(panelHost);
  const options = getPanelOptions(panelHost);

  const outcome = await runSimulateSessionWithDeps(sessionId, divisionLabel, sessionLabel, {
    confirm: (message) =>
      openConfirmModal(panelHost, {
        title: 'Simuler la session',
        message,
        confirmLabel: 'Simuler',
        cancelLabel: 'Annuler',
      }),
    simulate: async (id) => {
      const liveHost = resolveLiveChampionshipHost(panelHost);
      const dismiss = mountEarthLoaderOverlay(liveHost, 'Simulation de la course…');
      try {
        return await simulateMeetingSession(id);
      } finally {
        dismiss();
      }
    },
    onResults: (response) => {
      showChampionshipPostSessionFlow(response);
    },
    paintGlobalView: async (view) => {
      const liveHost = resolveLiveChampionshipHost(panelHost);
      await enqueueHostRender(liveHost, () => {
        paintChampionshipPanel(liveHost, { ...options, skipInitialLoader: true }, view, []);
      });
    },
    onError: (error) =>
      showChampionshipAlert(panelHost, error instanceof Error ? error.message : 'Erreur'),
    isInFlight: () => simulateInFlightByKey.get(hostKey) === true,
    setInFlight: (value) => {
      if (value) simulateInFlightByKey.set(hostKey, true);
      else simulateInFlightByKey.delete(hostKey);
    },
  });

  if (outcome === 'success') {
    window.dispatchEvent(new CustomEvent('multirx-championship-updated'));
  }
}

function renderMeetingCountPicker(
  selected: SeasonMeetingCount,
  onChange: (count: SeasonMeetingCount) => void
): HTMLElement {
  const wrap = document.createElement('fieldset');
  wrap.className = 'championship-meeting-picker';
  const legend = document.createElement('legend');
  legend.textContent = 'Nombre de meetings par saison';
  wrap.appendChild(legend);

  const hint = document.createElement('p');
  hint.className = 'championship-meeting-picker-hint';
  hint.textContent =
    'Chaque division compte 24 pilotes. Chaque meeting = qualifs (4 manches × 4 courses de 6 pilotes) + demi-finales + finale. Choix définitif pour cette campagne.';
  wrap.appendChild(hint);

  const options = document.createElement('div');
  options.className = 'championship-meeting-picker-options';
  options.setAttribute('role', 'radiogroup');
  options.setAttribute('aria-label', 'Nombre de meetings');

  for (const count of SEASON_MEETING_COUNT_OPTIONS) {
    const label = document.createElement('label');
    label.className = `championship-meeting-option${count === selected ? ' is-selected' : ''}`;
    const input = document.createElement('input');
    input.type = 'radio';
    input.name = 'season-meeting-count';
    input.value = String(count);
    input.checked = count === selected;
    input.addEventListener('change', () => {
      if (!input.checked) return;
      onChange(count);
      options.querySelectorAll('.championship-meeting-option').forEach((el) => {
        el.classList.toggle('is-selected', el === label);
      });
    });
    const title = document.createElement('span');
    title.className = 'championship-meeting-option-title';
    title.textContent = `${count} meetings`;
    const sub = document.createElement('span');
    sub.className = 'championship-meeting-option-sub';
    sub.textContent =
      count === 4
        ? 'Saison courte — barème et montées adaptés.'
        : 'Saison standard — format rallycross complet.';
    label.append(input, title, sub);
    options.appendChild(label);
  }

  wrap.appendChild(options);
  return wrap;
}

function renderDivisionTrackCard(
  divisionId: DivisionId,
  label: string,
  view: ActiveSeasonView | null,
  panelHost: HTMLElement,
  registrationStatus: SeasonRegistrationStatusView | null = null
): HTMLElement {
  const card = document.createElement('article');
  card.className = 'championship-division-card';
  card.dataset.division = String(divisionId);

  if (!view) {
    const title = document.createElement('h3');
    title.className = `championship-division-card-title championship-division-card-title--${divisionId}`;
    title.textContent = label;
    card.appendChild(title);
    const p = document.createElement('p');
    p.className = 'championship-panel-meta';
    p.textContent = 'Piste inactive.';
    card.appendChild(p);
    return card;
  }

  const next = view.nextSession;
  const meeting = view.currentMeeting;

  let circuitLine: string | null = null;
  let meetingLine: string | null = null;
  let sessionLine: string | null = null;

  if (next) {
    circuitLine = circuitTitleForCard(view, next);
    if (meeting) {
      meetingLine = formatDivisionMeetingLabel(meeting.meetingNumber, view.season.meetingCount);
    }
    sessionLine = `${next.label} · ${next.numLaps} tours`;
  } else if (view.season.status === 'registration') {
    const count = view.season.registrationCount ?? 0;
    sessionLine = `Inscriptions ouvertes — ${count}/${DRIVERS_PER_DIVISION}`;
  } else if (view.season.status === 'completed') {
    sessionLine = 'Division terminée.';
  } else {
    sessionLine = meeting ? `Meeting ${meeting.meetingNumber} en cours…` : 'En attente…';
  }

  appendDivisionCardProfileShell(card, view, {
    division: divisionId,
    divisionLabel: label,
    circuitLine,
    meetingLine,
    sessionLine,
  });

  if (next) {
    const actions = document.createElement('div');
    actions.className = 'championship-division-actions';
    const authUser = getCurrentUser();
    const isStaff = authUser?.role === 'admin' || authUser?.role === 'moderator';

    if (next.boardWarmupPending) {
      const warmupBtn = createPillButton('Tour de chauffe', {
        variant: 'cta',
        small: true,
        class: 'championship-division-card__btn championship-division-card__btn--solid',
      });
      if (warmupBtn instanceof HTMLButtonElement) {
        const inGrid = next.grid.some((slot) => slot.driverId === authUser?.driverId);
        if (isStaff) {
          warmupBtn.onclick = () => openWarmupModeModal(next.id, label, next.label, panelHost);
        } else if (inGrid) {
          warmupBtn.onclick = () => openPlayFlow(next.id, { skipModeChoice: true });
        } else {
          warmupBtn.disabled = true;
          warmupBtn.title = 'Votre pilote ne participe pas à cette manche.';
        }
      }
      actions.append(warmupBtn);
    } else {
      const playBtn = createPillButton('Jouer', {
        variant: 'cta',
        small: true,
        class: 'championship-division-card__btn championship-division-card__btn--solid',
      });
      if (playBtn instanceof HTMLButtonElement) {
        const inGrid = next.grid.some((slot) => slot.driverId === authUser?.driverId);
        if (isStaff || inGrid || authUser?.driverId == null) {
          playBtn.onclick = () => openPlayFlow(next.id);
        } else {
          playBtn.disabled = true;
          playBtn.title = 'Votre pilote ne participe pas à cette manche.';
        }
      }
      const simBtn = createPillButton('Simuler', {
        variant: 'soft',
        small: true,
        class: 'championship-division-card__btn',
      });
      if (simBtn instanceof HTMLButtonElement) {
        simBtn.dataset.championshipSimulate = '1';
        simBtn.dataset.sessionId = String(next.id);
        simBtn.dataset.divisionLabel = label;
        simBtn.dataset.sessionLabel = next.label;
        simBtn.dataset.role = 'admin';
      }
      actions.append(simBtn, playBtn);
    }
    card.appendChild(actions);
  } else if (view.season.status === 'registration') {
    const authUser = getCurrentUser();
    const joinAction = seasonJoinCardAction(divisionId, {
      hasDriver: authUser?.driverId != null,
      registeredDivision:
        registrationStatus?.registered === true ? registrationStatus.division : null,
    });

    if (joinAction !== 'hidden') {
      const actions = document.createElement('div');
      actions.className = 'championship-division-actions';
      if (joinAction === 'registered') {
        const badge = document.createElement('span');
        badge.className = 'championship-panel-meta';
        badge.textContent = 'Déjà inscrit';
        actions.appendChild(badge);
      } else {
        const joinBtn = createPillButton("S'inscrire", {
          variant: 'cta',
          small: true,
          class: 'championship-division-card__btn championship-division-card__btn--solid',
        });
        if (joinBtn instanceof HTMLButtonElement) {
          joinBtn.onclick = () => {
            joinBtn.disabled = true;
            void (async () => {
              try {
                await joinSeasonRegistration(divisionId);
                await renderChampionshipPanel(panelHost, {
                  ...getPanelOptions(panelHost),
                  preloadedGlobalView: undefined,
                  skipInitialLoader: true,
                });
              } catch (e) {
                showChampionshipAlert(panelHost, e instanceof Error ? e.message : 'Erreur');
                joinBtn.disabled = false;
              }
            })();
          };
        }
        actions.appendChild(joinBtn);
      }
      card.appendChild(actions);
    }
  }

  return card;
}

async function closeActiveSeasonAndRefresh(host: HTMLElement): Promise<void> {
  await cancelActiveGlobalSeason();
  // Toujours re-fetcher : ne jamais réutiliser un `preloadedGlobalView` périmé
  // (sinon l’écran reste bloqué sur « Saison terminée » sans bouton de création).
  const stored = getPanelOptions(host);
  const freshOptions: ChampionshipPanelOptions = { ...stored };
  delete freshOptions.preloadedGlobalView;
  await renderChampionshipPanel(host, {
    ...freshOptions,
    skipInitialLoader: true,
  });
  window.dispatchEvent(new CustomEvent('multirx-championship-updated'));
}

function createCancelSeasonButton(
  globalView: GlobalActiveSeasonView,
  host: HTMLElement,
  _options: ChampionshipPanelOptions
): HTMLButtonElement {
  const isCompleted = globalView.status === 'completed';
  const isRegistration = globalView.status === 'registration';
  const cancelBtn = createPillButton(isCompleted ? 'Nouvelle saison' : 'Abandonner la saison', {
    variant: isCompleted ? 'cta' : 'soft',
    small: true,
    class: 'championship-cancel-btn',
  });
  if (cancelBtn instanceof HTMLButtonElement) {
    cancelBtn.onclick = () => {
      const msg = isCompleted
        ? 'Clôturer cette campagne (elle reste dans l’Historique) et préparer une nouvelle saison ?'
        : isRegistration
          ? 'Annuler la saison en cours d’inscription ?\n\nLa coquille et les inscriptions déjà enregistrées seront supprimées définitivement.'
          : `Abandonner la campagne en cours (meeting ${globalView.currentMeetingIndex} / ${globalView.meetingCount}) ?\n\nLa saison sera supprimée définitivement (aucune trace dans l’historique). Vous pourrez en lancer une nouvelle avec un autre nombre de meetings.`;
      void openConfirmModal(host, {
        title: isCompleted ? 'Nouvelle saison' : 'Abandonner la saison',
        message: msg,
        confirmLabel: isCompleted ? 'Clôturer' : 'Abandonner',
        cancelLabel: 'Annuler',
        danger: !isCompleted,
      }).then((confirmed) => {
        if (!confirmed) return;
        const dismiss = mountEarthLoaderOverlay(
          host,
          isCompleted ? 'Clôture de la saison…' : 'Annulation de la saison…'
        );
        void (async () => {
          try {
            await closeActiveSeasonAndRefresh(host);
          } catch (e) {
            showChampionshipAlert(host, e instanceof Error ? e.message : 'Erreur');
          } finally {
            dismiss();
          }
        })();
      });
    };
    return cancelBtn;
  }
  return document.createElement('button');
}

function buildChampionshipStatusLine(globalView: GlobalActiveSeasonView): HTMLParagraphElement {
  const status = document.createElement('p');
  status.className = 'championship-status-line';
  const divisionMeetingIndices = Object.values(globalView.divisions)
    .filter((v): v is ActiveSeasonView => v != null)
    .map((v) => v.season.currentMeetingIndex);
  status.textContent =
    globalView.status === 'completed'
      ? `Saison terminée — ${globalView.meetingCount} meetings joués.`
      : globalView.status === 'registration'
        ? `Inscriptions ouvertes — ${globalView.meetingCount} meetings prévus.`
        : formatGlobalMeetingProgress(divisionMeetingIndices, globalView.meetingCount);
  return status;
}

function appendPanelTitle(
  panel: HTMLElement,
  titleText: string,
  globalView: GlobalActiveSeasonView | null,
  host: HTMLElement,
  options: ChampionshipPanelOptions
): void {
  const head = document.createElement('div');
  head.className = 'championship-panel-head';
  const title = document.createElement('h2');
  title.textContent = titleText;
  head.appendChild(title);
  if (globalView) {
    head.append(
      buildChampionshipStatusLine(globalView),
      createCancelSeasonButton(globalView, host, options)
    );
  }
  options.appendHeaderActions?.(head, { globalView });
  panel.appendChild(head);
}

function paintChampionshipPanel(
  host: HTMLElement,
  options: ChampionshipPanelOptions,
  globalView: GlobalActiveSeasonView | null,
  circuitOptions: CircuitPickerOption[],
  registrationStatus: SeasonRegistrationStatusView | null = null
): void {
  const titleText = options.title ?? championshipPanelTitle(globalView);
  const panelId = options.panelId ?? 'championship-panel-heading';

  setPanelOptions(host, options);
  host.dataset.championshipPanelHost = '1';
  ensureSimulateDelegation(host);

  beginHostRenderTask(host);
  host.innerHTML = '';
  if (options.hostClassName) host.className = options.hostClassName;

  const panel = document.createElement('div');
  panel.className = 'dashboard-panel dashboard-panel--championship';
  panel.id = panelId;

  let selectedMeetingCount = getPreferredMeetingCount();
  let selectedCalendar = buildInitialCircuitCalendar(circuitOptions, selectedMeetingCount);

  appendPanelTitle(panel, titleText, globalView, host, options);

  if (!globalView) {
    const empty = document.createElement('div');
    empty.className = 'championship-empty-state';
    const p = document.createElement('p');
    p.className = 'championship-empty-text';
    p.textContent =
      'Aucune saison en cours. Choisissez le format, le calendrier des circuits, puis lancez les quatre divisions.';
    empty.appendChild(p);

    const circuitHost = document.createElement('div');
    circuitHost.className = 'championship-circuit-picker-host';

    const startBtn = createPillButton('Créer la saison', {
      variant: 'cta',
      large: true,
      class: 'championship-start-btn',
    });

    const syncStartButton = (): void => {
      if (!(startBtn instanceof HTMLButtonElement)) return;
      const complete = isCircuitPickerComplete({
        meetingCount: selectedMeetingCount,
        circuits: circuitOptions,
        calendar: selectedCalendar,
      });
      startBtn.disabled = !complete;
    };

    const mountCircuitPicker = (): void => {
      circuitHost.replaceChildren(
        renderSeasonCircuitPicker(
          {
            meetingCount: selectedMeetingCount,
            circuits: circuitOptions,
            calendar: selectedCalendar,
          },
          (calendar) => {
            selectedCalendar = calendar;
            syncStartButton();
            mountCircuitPicker();
          }
        )
      );
      syncStartButton();
    };

    empty.appendChild(
      renderMeetingCountPicker(selectedMeetingCount, (count) => {
        selectedMeetingCount = count;
        setPreferredMeetingCount(count);
        selectedCalendar = trimCalendarToMeetingCount(selectedCalendar, count);
        if (selectedCalendar.length < count && circuitOptions.length >= count) {
          const missing = count - selectedCalendar.length;
          const used = new Set(selectedCalendar);
          const extras = circuitOptions
            .map((c) => c.id)
            .filter((id) => !used.has(id))
            .slice(0, missing);
          selectedCalendar = [...selectedCalendar, ...extras];
        }
        mountCircuitPicker();
      })
    );
    mountCircuitPicker();
    empty.appendChild(circuitHost);

    if (startBtn instanceof HTMLButtonElement) {
      syncStartButton();
      startBtn.onclick = () => {
        const dismiss = mountEarthLoaderOverlay(host, 'Préparation de la saison…');
        empty.classList.add('is-loading');
        void (async () => {
          try {
            const view = await createGlobalSeasonShell(selectedMeetingCount, selectedCalendar);
            const regStatus = await fetchSeasonRegistrationStatus().catch(() => null);
            await enqueueHostRender(host, () => {
              paintChampionshipPanel(
                host,
                { ...options, skipInitialLoader: true },
                view,
                circuitOptions,
                regStatus
              );
            });
            window.dispatchEvent(new CustomEvent('multirx-championship-updated'));
          } catch (e) {
            showChampionshipAlert(host, e instanceof Error ? e.message : 'Erreur');
          } finally {
            dismiss();
            empty.classList.remove('is-loading');
          }
        })();
      };
    }
    empty.appendChild(startBtn);
    panel.appendChild(empty);
    host.appendChild(panel);
    return;
  }

  const blurb = document.createElement('p');
  blurb.className = 'championship-season-blurb';
  blurb.textContent =
    `D1 à D4 en parallèle (24 pilotes chacune). Chaque meeting : qualifications (4 manches), demi-finales puis finale. ` +
    `La saison compte ${globalView.meetingCount} meetings ; les points cumulés désignent le champion de chaque division.`;
  panel.appendChild(blurb);

  if (globalView.status === 'completed') {
    const doneBanner = document.createElement('div');
    doneBanner.className = 'championship-completed-banner';
    doneBanner.innerHTML =
      '<p class="championship-completed-banner__text">Campagne terminée — consultez les classements ci-dessous, puis clôturez pour en lancer une nouvelle.</p>';
    const newSeasonBtn = createPillButton('Nouvelle saison', {
      variant: 'cta',
      small: true,
      class: 'championship-completed-banner__btn',
    });
    if (newSeasonBtn instanceof HTMLButtonElement) {
      newSeasonBtn.onclick = () => {
        const cancelBtn = host.querySelector<HTMLButtonElement>('.championship-cancel-btn');
        cancelBtn?.click();
      };
    }
    doneBanner.appendChild(newSeasonBtn);
    panel.appendChild(doneBanner);
  }

  if (globalView.status === 'registration') {
    const authUser = getCurrentUser();
    const isStaff = authUser?.role === 'admin' || authUser?.role === 'moderator';
    if (isStaff) {
      const lockBtn = createPillButton('Verrouiller les inscriptions', {
        variant: 'cta',
        small: true,
        class: 'championship-lock-registration-btn',
      });
      if (lockBtn instanceof HTMLButtonElement) {
        lockBtn.onclick = () => {
          void openConfirmModal(host, {
            title: 'Verrouiller les inscriptions',
            message:
              'Générer le roster et la première manche pour les 4 divisions ?\n\nLes places restantes seront comblées par des pilotes IA. Cette action est irréversible.',
            confirmLabel: 'Verrouiller',
            cancelLabel: 'Annuler',
          }).then((confirmed) => {
            if (!confirmed) return;
            lockBtn.disabled = true;
            const dismiss = mountEarthLoaderOverlay(host, 'Verrouillage des inscriptions…');
            void (async () => {
              try {
                await lockAndStartGlobalSeason();
                await renderChampionshipPanel(host, {
                  ...getPanelOptions(host),
                  preloadedGlobalView: undefined,
                  skipInitialLoader: true,
                });
                window.dispatchEvent(new CustomEvent('multirx-championship-updated'));
              } catch (e) {
                showChampionshipAlert(host, e instanceof Error ? e.message : 'Erreur');
                lockBtn.disabled = false;
              } finally {
                dismiss();
              }
            })();
          });
        };
      }
      panel.appendChild(lockBtn);
    }
  }

  const grid = document.createElement('div');
  grid.className = 'championship-divisions-stack';

  for (const d of DIVISIONS) {
    const track = globalView.divisions[d.id];
    grid.appendChild(
      renderDivisionTrackCard(d.id, d.name, track ?? null, host, registrationStatus)
    );
  }
  panel.appendChild(grid);

  host.appendChild(panel);
}

function buildInitialCircuitCalendar(
  circuits: CircuitPickerOption[],
  meetingCount: SeasonMeetingCount
): number[] {
  const ids = circuits.map((c) => c.id);
  if (ids.length < meetingCount) return [];
  return pickSeasonCircuitCalendar(ids, meetingCount);
}

function mapCircuitOptions(
  circuits: Awaited<ReturnType<typeof fetchCircuits>>
): CircuitPickerOption[] {
  // Seuls les circuits publiés entrent dans la rotation d'une saison — les circuits
  // archivés (retirés volontairement) ainsi que ceux en attente/rejetés (jamais
  // validés) ne doivent jamais y apparaître.
  return circuits
    .filter((c) => (c.status ?? 'published') === 'published')
    .map((c) => ({
      id: c.id,
      name: c.name?.trim() ? c.name.trim() : `Circuit #${c.id}`,
    }));
}

export async function renderChampionshipPanel(
  host: HTMLElement,
  options: ChampionshipPanelOptions = {}
): Promise<void> {
  const liveHost = resolveLiveChampionshipHost(host);
  return enqueueHostRender(liveHost, async () => {
    const taskId = beginHostRenderTask(liveHost);
    let dismissInitial: (() => void) | null = null;
    if (!options.skipInitialLoader) {
      dismissInitial = mountEarthLoaderOverlay(liveHost, 'Chargement du championnat…');
    }
    let globalView: GlobalActiveSeasonView | null = null;
    let circuitOptions: CircuitPickerOption[] = [];
    let registrationStatus: SeasonRegistrationStatusView | null = null;
    try {
      if (options.preloadedGlobalView !== undefined) {
        globalView = options.preloadedGlobalView;
        if (!globalView) {
          circuitOptions = mapCircuitOptions(await fetchCircuits(100));
        }
      } else {
        globalView = await fetchActiveGlobalSeason();
        if (!globalView) {
          circuitOptions = mapCircuitOptions(await fetchCircuits(100));
        }
      }
      if (globalView?.status === 'registration') {
        registrationStatus = await fetchSeasonRegistrationStatus().catch(() => null);
      }
    } catch {
      globalView = null;
    } finally {
      dismissInitial?.();
    }

    if (!isLatestHostRenderTask(liveHost, taskId)) {
      return;
    }
    paintChampionshipPanel(liveHost, options, globalView, circuitOptions, registrationStatus);
    applyRoleVisibility();
  });
}
